import { Rule } from 'antd/lib/form'

import {
  SelectCaptureBlock as SelectCaptureBlockType,
  SelectOptionsField,
} from '../../types/blueprint'

import { CaptureBlockFormItem } from './CaptureBlockFormItem'

import { Select, Option } from '@/components/shared-components/BUI/Select'

type Option = {
  children: SelectCaptureBlockType['options'][number]['value']
}

type Props = {
  block: SelectCaptureBlockType
  rules: Rule[]
}

const getValue = (
  options: SelectOptionsField[],
  defaultOption: string,
  allowOther: boolean,
  multiple: boolean
) => {
  const selectedOptions = options.filter((o) => o.selected)
  if (selectedOptions?.length > 0) {
    return allowOther || multiple ? selectedOptions : selectedOptions[0]
  }

  if (defaultOption) {
    const foundOption = options.find((o) => o.label === defaultOption)
    if (foundOption) {
      return multiple ? [foundOption] : foundOption
    }
  }

  return undefined
}

export const SelectCaptureBlock: React.FC<Props> = ({ block, rules }) => {
  const {
    options = [],
    defaultOption,
    multiSelect = false,
    allowOther = false,
  } = block

  return (
    <CaptureBlockFormItem
      block={block}
      initialValue={getValue(options, defaultOption, allowOther, multiSelect)}
      rules={rules}
    >
      <CustomSelect block={block} />
    </CaptureBlockFormItem>
  )
}

type CustomSelectProps = {
  block: SelectCaptureBlockType
  value?: string | string[]
  onChange?: (value: string | string[]) => void
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  block,
  value,
  onChange,
}) => {
  const {
    options = [],
    multiSelect = false,
    searchable = false,
    allowOther = false,
  } = block

  const onChangeValue = (value: string | string[]) => {
    // tags imply multi-select so need to manually pick the last value
    if (allowOther && !multiSelect && Array.isArray(value)) {
      onChange?.(value[value.length - 1])
      return
    }
    onChange?.(value)
  }

  return (
    <Select
      showArrow
      placeholder=""
      allowClear
      labelInValue
      mode={allowOther ? 'tags' : multiSelect ? 'multiple' : undefined}
      showSearch={searchable}
      filterOption={(input: string, option: Option) =>
        option?.children &&
        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
      }
      value={value}
      onChange={onChangeValue}
    >
      {options.map((option, i) => (
        <Option key={i} value={option.value}>
          {option.label}
        </Option>
      ))}
    </Select>
  )
}
